import { useEffect, useState } from 'react';

import { isReviewApp } from 'app/lib/environmentChecker';

interface CookiebotConsent {
  consentReady: boolean;
  necessaryConsent: boolean;
  preferencesConsent: boolean;
  statisticsConsent: boolean;
  marketingConsent: boolean;
}

const FULL_CONSENT: CookiebotConsent = {
  consentReady: true,
  necessaryConsent: true,
  preferencesConsent: true,
  statisticsConsent: true,
  marketingConsent: true,
};

const getCookiebot = () =>
  typeof window !== 'undefined' ? window.Cookiebot : undefined;

// consentReady is true once the visitor has answered the banner (now, or on an earlier visit), or
// consent is implied (given automatically, e.g. where the banner is opt-out). It doesn't just check
// that a consent object exists, because Cookiebot creates one before the visitor answers. Cookiebot
// may also not exist yet, e.g. when this runs above withCookieBot on the first render.
const readCookiebotConsent = (): CookiebotConsent => {
  const cookiebot = getCookiebot();
  const consent = cookiebot?.consent;
  const ready = !!cookiebot?.hasResponse || consent?.method === 'implied';

  return {
    consentReady: ready,
    necessaryConsent: !!consent?.necessary, // Does not always require a response
    preferencesConsent: ready && !!consent?.preferences,
    statisticsConsent: ready && !!consent?.statistics,
    marketingConsent: ready && !!consent?.marketing,
  };
};

const isCookiebotConsentRequired = () =>
  process.env.NODE_ENV === 'production' && !isReviewApp();

// Reads consent at the moment it's called, for code that runs outside React's render, like
// sending an analytics event
export const getCookiebotConsent = (): CookiebotConsent =>
  isCookiebotConsentRequired() ? readCookiebotConsent() : FULL_CONSENT;

export const useCookiebotConsent = (): CookiebotConsent => {
  const shouldRequireCookiebotConsent = isCookiebotConsentRequired();

  const [consent, setConsent] = useState<CookiebotConsent>(getCookiebotConsent);

  useEffect(() => {
    // Cookiebot may have already fired before this effect registered listeners
    // (eg script loaded from cache for a returning visitor). Check synchronously
    // on mount so we don't miss that case
    if (shouldRequireCookiebotConsent) {
      const currentConsent = readCookiebotConsent();
      if (currentConsent.consentReady) {
        setConsent(currentConsent);
      }
    }

    // Each of these events means the visitor's choice is known
    const handleConsentReady = () => {
      setConsent({ ...readCookiebotConsent(), consentReady: true });
    };

    window.addEventListener('CookiebotOnConsentReady', handleConsentReady);
    window.addEventListener('CookiebotOnAccept', handleConsentReady);
    window.addEventListener('CookiebotOnDecline', handleConsentReady);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', handleConsentReady);
      window.removeEventListener('CookiebotOnAccept', handleConsentReady);
      window.removeEventListener('CookiebotOnDecline', handleConsentReady);
    };
  }, [shouldRequireCookiebotConsent]);

  return consent;
};
